import { Flex, Text, WarningIcon } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { isBefore2025 } from '@/features/project/_utils/project';
import { FundAdditionalIncomeAndExpenditureModel } from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit, isPS } from '@/utils';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  type FundIncomeAndExpenditureDataKey,
  getIncomeAndExpenditureSummaryByType,
} from '../../../_utils/fund-income-and-expenditure-table';

export const CellValue = ({
  value,
  dataKey,
  targetYear,
  fiscalYear,
  data,
}: {
  value: number | string | null | undefined;
  dataKey: FundIncomeAndExpenditureDataKey;
  targetYear: 'previousYear' | 'currentYear' | 'nextYear' | null;
  fiscalYear: number;
  data: z.infer<typeof FundAdditionalIncomeAndExpenditureModel>;
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;
  if (
    // 前年度末基金残高
    dataKey === 'fund-balance-at-the-end-of-previous-fiscal-year' &&
    !isBefore2025({ fiscalYear }) &&
    !isPS()
  ) {
    const calculatedPreviousValueByTargetYear = (() => {
      if (targetYear === null) {
        return;
      }
      if (targetYear === 'nextYear') {
        return getIncomeAndExpenditureSummaryByType({
          fundAdditionalIncomeAndExpenditure: data,
          type: 'estimated-this-year',
          isDebugMode,
        });
      }
      if (targetYear === 'currentYear') {
        if (data.prior) {
          return getIncomeAndExpenditureSummaryByType({
            fundAdditionalIncomeAndExpenditure: data.prior,
            type: 'previous-year-performance',
            isDebugMode,
          });
        }
      }
      if (targetYear === 'previousYear') {
        const parsedPrior = FundAdditionalIncomeAndExpenditureModel.safeParse(
          data.prior,
        );
        if (parsedPrior.success && parsedPrior.data.prior) {
          return getIncomeAndExpenditureSummaryByType({
            fundAdditionalIncomeAndExpenditure: parsedPrior.data.prior,
            type: 'previous-year-performance',
            isDebugMode,
          });
        }
      }
    })();

    if (
      calculatedPreviousValueByTargetYear !== undefined &&
      !calculatedPreviousValueByTargetYear?.isAggregateValueZero &&
      value !== calculatedPreviousValueByTargetYear?.value
    ) {
      return (
        <Flex justify="end" gap={4}>
          <WarningIcon
            hint={t('warning-message-different-from-reference-value')}
          />
          <Text
            weight="regular"
            text={
              typeof value === 'number'
                ? formatValueByUnit(value, { unit, operation: 'round' })
                : value
            }
          />
        </Flex>
      );
    }
  }

  return (
    <Text
      weight="regular"
      text={
        typeof value === 'number'
          ? formatValueByUnit(value, { unit, operation: 'round' })
          : value || null
      }
    />
  );
};
